import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MessagingPanel from '../components/MessagingPanel';

/**
 * Standalone Messages Page — accessible at /messages
 * Wraps the MessagingPanel component as a full-page view.
 * Supports ?conversationId=xxx query param to auto-open a specific conversation.
 */
export default function MessagesPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [searchParams] = useSearchParams();
  const conversationId = searchParams.get('conversationId') || null;

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-sm">
          <div className="text-5xl mb-4">💬</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Sign in to view messages</h2>
          <p className="text-gray-500 text-sm mb-4">You need to be logged in to access your conversations.</p>
          <button
            onClick={() => navigate('/login')}
            className="w-full py-2.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  // Determine the user role for the messaging panel
  // Delivery riders are the "host" in delivery conversations, so treat them as host
  // so MessagingPanel shows guest names, rider unread counts, and own message styling correctly
  const roles = user?.roles || [];
  let userRole = 'guest'; // default
  if (roles.includes('delivery_rider')) userRole = 'host';
  else if (roles.includes('restaurant_partner')) userRole = 'restaurant';
  else if (roles.includes('taxi_driver')) userRole = 'driver';
  else if (roles.includes('host')) userRole = 'host';
  else if (roles.includes('guest')) userRole = 'guest';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-blue-600 font-medium text-sm hover:text-blue-700"
          >
            <span className="text-lg">←</span> Back
          </button>
          <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            💬 Messages
          </h1>
        </div>
      </div>

      {/* Messaging Panel */}
      <div className="max-w-4xl mx-auto" style={{ height: 'calc(100vh - 60px)' }}>
        <MessagingPanel
          userRole={userRole}
          autoSelectConversationId={conversationId}
        />
      </div>
    </div>
  );
}
